import _ from "lodash";
import { MODALITY_TYPE_INFO_MAPPING } from "./Constants";
import { getIsChina } from "config";
import moment from "moment";

// Constants
import {
  ANALYSING,
  GENERATING,
  THINKING,
  SEARCHING,
  WRITING,
  STREAMING,
  LOADING,
  BROWSING_THE_INTERNET,
} from "Constants/stringConstants";
import {
  AI_TUTOR_TOOL_NAME_STATE_MAPPING,
  ANNOUNCEMENT_TOOL_NAME_STATE_MAPPING,
} from "Utils/AIUtils";
import { getIsReactPlayerVideoModalityEnabled } from "Utils/ACLStoreUtils";

export const getAttachmentId = attachment => {
  const id = attachment?.originalAttachmentId || attachment?.id;
  return _.startsWith(id, "NEW_") ? null : id;
};

/**
 * Checks if an attachment is a valid modality attachment.
 * For LINK type, it also checks if the URL is a YouTube link.
 * @param {Object} attachment - The attachment object with type and url properties
 * @param {boolean} [disableAttachmentIdCheck=false] - If true, skips getAttachmentId check
 * @returns {boolean} - Whether the attachment is a valid modality
 */
export const isModalityAttachmentType = (
  attachment,
  disableAttachmentIdCheck = false,
  typeToCheck
) => {
  const { type, url, metadata } = attachment || {};

  // If a specific type is requested, only allow that type
  if (typeToCheck && type !== typeToCheck) {
    return false;
  }

  const modalityTypes = _.keys(MODALITY_TYPE_INFO_MAPPING);

  const invalidModalityAttachment =
    !_.includes(modalityTypes, type) ||
    (!disableAttachmentIdCheck && !getAttachmentId(attachment));

  if (invalidModalityAttachment) return false;

  // For LINK type, check if it's a YouTube link
  if (type === "LINK") {
    return (
      (!getIsChina() || getIsReactPlayerVideoModalityEnabled()) &&
      !_.isEmpty(metadata?.checkPoints)
    );
  }

  return true;
};

export const getTimeSince = (t, date) => {
  const now = moment(new Date());
  const momentDate = moment(date);
  const diffInSeconds = now.diff(momentDate, "seconds");
  const diffInMin = now.diff(momentDate, "minutes");
  const diffInHours = now.diff(momentDate, "hours");

  if (diffInHours >= 24) {
    return moment(date).format("DD MMM YYYY, h:mm a");
  } else if (diffInHours < 1) {
    if (diffInMin < 1) {
      if (diffInSeconds < 6) {
        return t("common:now_uppercase");
      }
      return t("classRoom:second_abb_ago_with_count", { count: diffInSeconds });
    }
    return t("classRoom:minute_abb_ago_with_count", { count: diffInMin });
  }
  return t("classRoom:hour_abb_ago_with_count", { count: diffInHours });
};

// Extract attachment IDs that are already embedded in the value HTML
export const extractAttachmentIdsFromValue = htmlString => {
  if (_.isEmpty(htmlString)) return [];

  const tempElement = document.createElement("div");
  tempElement.innerHTML = htmlString;

  const attachmentElements = tempElement.querySelectorAll(
    "[data-attachment-id]"
  );

  return _.map(attachmentElements, element =>
    _.get(element, "dataset.attachmentId")
  ).filter(id => id && id !== "undefined");
};

const TYPE_LABELS = {
  TODDLE_DOC: "Document",
  CODE_EDITOR: "Code editor",
};

export const getCustomType = attachment => {
  const type = attachment?.type;

  if (!type) return "";

  if (type === "LINK") {
    return isModalityAttachmentType(attachment) ? "Video" : "Link";
  }

  return TYPE_LABELS[type] || _.capitalize(_.toLower(type));
};

export const getIsAIResponseLoading = state => {
  return [
    ANALYSING,
    BROWSING_THE_INTERNET,
    SEARCHING,
    GENERATING,
    THINKING,
    WRITING,
    STREAMING,
    LOADING,
    ..._.values(AI_TUTOR_TOOL_NAME_STATE_MAPPING),
    ..._.values(ANNOUNCEMENT_TOOL_NAME_STATE_MAPPING),
  ].includes(state);
};
